<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<!-- Pipelines for a project, as an image x pipeline matrix.

     One row per (image, pipeline) pair rather than one row per run, because most of what
     this page has to answer is about what has NOT happened: which images have not been
     through a given pipeline, which have had nothing run on them at all. A list of runs
     cannot answer either — an image with no run has no row to show.

     So the rows are every run that exists (including runs of pipelines no longer offered,
     which are still real history), plus a "not run" row for each image x offered-pipeline
     pair that has none. Running one image through two pipelines gives it two rows, which
     is the intent. -->
<template>
<div class="pipelines-page content-wrapper">
  <b-message v-if="!canManageProject" type="is-warning" has-icon>
    {{ $t('pipelines-admin-only') }}
  </b-message>

  <template v-else>
    <div class="box">
      <div class="runs-head">
        <h2>{{ $t('pipelines-runs-title') }}</h2>
        <b-input
          v-model="search"
          type="search"
          icon="search"
          size="is-small"
          class="runs-search"
          :placeholder="$t('pipelines-search-placeholder')"
        />
      </div>

      <!-- Faceted filters. Within a facet the chips OR together (Succeeded + Failed shows
           both); across facets they AND (Succeeded + RFDetr_v1 shows succeeded RFDetr
           runs). Counts are computed against the other facets, so a chip never advertises
           a number you cannot actually reach by clicking it. -->
      <div class="runs-filters">
        <div v-for="facet in facets" :key="facet.key" class="facet">
          <span class="facet-label">{{ $t(facet.labelKey) }}</span>
          <button
            v-for="opt in facet.options"
            :key="opt.key"
            class="button is-small facet-chip"
            :class="{'is-info': isSelected(facet.key, opt.key)}"
            :disabled="!isSelected(facet.key, opt.key) && facetCount(facet.key, opt.key) === 0"
            @click="toggleFacet(facet.key, opt.key)"
          >
            {{ opt.label }}
            <span class="facet-count">{{ facetCount(facet.key, opt.key) }}</span>
          </button>
        </div>
        <button v-if="hasActiveFilters" class="button is-small is-text" @click="clearFilters">
          {{ $t('pipelines-filter-clear') }}
        </button>
      </div>

      <p class="help matrix-note">{{ $t('pipelines-matrix-note') }}</p>

      <!-- Filtered and paged in the browser: the endpoints return this project's images
           and runs in one call each, which is fine into the hundreds. If a tenant ever
           accumulates thousands of runs this needs to move server-side. -->
      <b-table
        :data="visibleRows"
        :loading="loading"
        paginated
        :per-page="perPage"
        :current-page.sync="currentPage"
        pagination-position="bottom"
      >
        <template #default="{row}">
          <b-table-column field="slide_name" :label="$t('pipelines-col-image')" sortable>
            <router-link
              v-if="row.image"
              :to="`/project/${project.id}/image/${row.image}`"
              :title="$t('pipelines-open-image')"
            >
              {{ row.slide_name || row.image }}
            </router-link>
            <span v-else>{{ row.slide_name }}</span>
          </b-table-column>

          <b-table-column field="pipeline" :label="$t('pipelines-col-pipeline')" sortable>
            {{ pipelineLabel(row.pipeline) }}
          </b-table-column>

          <b-table-column field="status" :label="$t('pipelines-col-status')" sortable>
            <b-tag :type="statusType(row.status)">{{ statusLabel(row) }}</b-tag>
          </b-table-column>

          <b-table-column field="progress" :label="$t('pipelines-col-progress')" width="220">
            <div v-if="isActive(row)" class="progress-cell">
              <b-progress
                :value="percent(row)"
                :type="progressType(row)"
                :indeterminate="percent(row) === null"
                size="is-small"
                show-value
                format="percent"
              />
              <p class="progress-note">
                <span v-if="row.status === 'DELETING'">
                  <template v-if="row.delete_total">{{ $t('pipelines-deleting-progress', {done: row.deleted_count, total: row.delete_total}) }}</template>
                  <template v-else>{{ $t('pipelines-deleting') }}</template>
                </span>
                <span v-else-if="isIngesting(row)">
                  <template v-if="row.total">{{ $t('pipelines-annotations-progress', {done: row.inserted, total: row.total}) }}</template>
                </span>
                <span v-else-if="row.tiles_total">{{ $t('pipelines-tiles', {done: row.tiles_done, total: row.tiles_total}) }}</span>
                <span v-if="etaLabel(row)"> &middot; {{ etaLabel(row) }}</span>
              </p>
            </div>
            <span v-else class="has-text-grey">&mdash;</span>
          </b-table-column>

          <b-table-column field="inserted" :label="$t('pipelines-col-inserted')">
            <span v-if="isRealRun(row)">
              {{ row.inserted }}<template v-if="row.total"> / {{ row.total }}</template>
            </span>
            <span v-else class="has-text-grey">&mdash;</span>
          </b-table-column>

          <b-table-column field="insertion_threshold" :label="$t('pipelines-col-confidence')">
            <span
              v-if="row.insertion_threshold !== null && row.insertion_threshold !== undefined"
              :title="$t('pipelines-confidence-help')"
            >
              &gt; {{ Math.round(row.insertion_threshold * 100) }}%
            </span>
            <span v-else class="has-text-grey">&mdash;</span>
          </b-table-column>

          <b-table-column field="error_reason" :label="$t('pipelines-col-detail')">
            <span v-if="row.error_reason" class="has-text-danger">{{ row.error_reason }}</span>
          </b-table-column>

          <b-table-column :label="$t('pipelines-col-actions')" width="260">
            <div class="buttons are-small run-actions">
              <button
                v-if="canRun(row)"
                class="button is-link"
                :disabled="running"
                @click="runRow(row)"
              >
                {{ runLabel(row) }}
              </button>
              <button
                v-if="row.has_mask"
                class="button is-light"
                :title="$t('pipelines-mask-help')"
                @click="downloadMask(row)"
              >
                {{ $t('pipelines-mask-download') }}
              </button>
              <button
                v-if="isActive(row) && row.status !== 'DELETING'"
                class="button is-warning is-light"
                :disabled="row.stop_requested"
                @click="stopRun(row)"
              >
                {{ $t('pipelines-stop') }}
              </button>
              <button v-if="canReapply(row)" class="button is-light" @click="openReapply(row)">
                {{ $t('pipelines-reapply') }}
              </button>
              <button
                v-if="isRealRun(row) && !isActive(row)"
                class="button is-danger is-light"
                @click="deleteRun(row)"
              >
                {{ $t('pipelines-delete-run') }}
              </button>
            </div>
          </b-table-column>
        </template>
        <template #empty>
          <p class="has-text-grey has-text-centered">
            {{ hasActiveFilters ? $t('pipelines-no-match') : $t('pipelines-no-runs') }}
          </p>
        </template>
      </b-table>
      <p class="help queue-note">{{ $t('pipelines-queue-note') }}</p>
    </div>

    <b-modal :active.sync="reapplyModal" has-modal-card>
      <div class="modal-card reapply-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('pipelines-reapply-title') }}</p>
        </header>
        <section class="modal-card-body">
          <p>{{ $t('pipelines-reapply-body') }}</p>
          <b-field grouped class="reapply-field">
            <b-field expanded>
              <b-slider v-model="reapplyPct" :min="30" :max="95" :step="5" :tooltip="false" ticks />
            </b-field>
            <p class="control threshold-value">{{ reapplyPct }}%</p>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <button class="button" @click="reapplyModal = false">{{ $t('button-cancel') }}</button>
          <button class="button is-link" @click="confirmReapply">{{ $t('pipelines-reapply') }}</button>
        </footer>
      </div>
    </b-modal>
  </template>
</div>
</template>

<script>
import {ImageInstanceCollection} from '@/api';
import pipelineService from '@/api/pipeline-service';
import {get} from '@/utils/store-helpers';

// Everything that is not a terminal state. Kept in one place because the progress column,
// the action buttons and the poller must all agree on what "still going" means.
const ACTIVE_STATES = [
  'PENDING', 'QUEUED', 'RUNNING', 'INFERRING', 'INGESTING', 'INGEST_RUNNING', 'REINGEST',
  // Deletion is non-terminal too: it takes ~27 s for 36,000 annotations and the row must
  // keep polling and keep showing a bar until it disappears.
  'DELETING',
];

const NOT_RUN = 'NOT_RUN';

// Default ordering: what is happening now, then what is about to happen, then history.
// Only one run executes at a time, so the row a user is waiting on must never be buried
// on page 3 behind slides that finished last week.
function sortRank(row, isActive) {
  if (row.status === 'QUEUED') {
    return 1;
  }
  if (isActive) {
    return 0;                       // in progress
  }
  if (row.status === 'FAILED') {
    return 2;                       // needs attention next
  }
  if (row.status === NOT_RUN) {
    return 4;
  }
  return 3;
}

export default {
  name: 'pipelines-page',
  data() {
    return {
      catalog: [],
      images: [],
      runs: [],
      search: '',
      currentPage: 1,
      perPage: 15,
      selected: {status: [], pipeline: [], artifacts: []},
      loading: true,
      running: false,
      pollTimer: null,
      reapplyModal: false,
      reapplyPct: 70,
      reapplyRun: null,
    };
  },
  computed: {
    project: get('currentProject/project'),
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    hasActiveRun() {
      return this.runs.some(r => ACTIVE_STATES.includes(r.status));
    },
    runsByImage() {
      let map = {};
      this.runs.forEach(r => {
        (map[r.image] = map[r.image] || []).push(r);
      });
      return map;
    },
    /** Every run, plus a "not run" row for each image x offered-pipeline pair without one. */
    allRows() {
      let rows = [...this.runs];
      let byImage = this.runsByImage;
      this.images.forEach(img => {
        let done = new Set((byImage[img.id] || []).map(r => r.pipeline));
        this.catalog.forEach(p => {
          if (done.has(p.key)) {
            return;
          }
          /* eslint-disable camelcase */
          // snake_case deliberately: shape-compatible with what the API returns, because
          // the template and every helper read those field names.
          rows.push({
            run_uuid: `not-run-${img.id}-${p.key}`,
            image: img.id,
            slide_name: img.instanceFilename,
            pipeline: p.key,
            status: NOT_RUN,
            inserted: 0,
            total: 0,
            insertion_threshold: null,
            tiles_done: 0,
            tiles_total: 0,
            has_mask: false,
            has_results: false,
          });
          /* eslint-enable camelcase */
        });
      });
      return rows;
    },
    facets() {
      let out = [{
        key: 'status',
        labelKey: 'pipelines-filter-status',
        options: [
          {key: 'succeeded', label: this.$t('pipelines-filter-succeeded')},
          {key: 'running', label: this.$t('pipelines-filter-running')},
          {key: 'failed', label: this.$t('pipelines-filter-failed')},
          {key: 'stopped', label: this.$t('pipelines-filter-stopped')},
          {key: 'not-run', label: this.$t('pipelines-filter-not-run')},
          // "Nothing at all has been run on this image", which is a different question
          // from "this one pipeline has not been run on it".
          {key: 'untouched', label: this.$t('pipelines-filter-untouched')},
        ],
      }];
      // Every pipeline present, whether currently offered or only in history — a run of a
      // retired pipeline is still something you may want to find.
      let keys = [...new Set([
        ...this.catalog.map(p => p.key),
        ...this.runs.map(r => r.pipeline).filter(Boolean),
      ])];
      if (keys.length > 1) {
        out.push({
          key: 'pipeline',
          labelKey: 'pipelines-filter-pipeline',
          options: keys.map(k => ({key: k, label: this.pipelineLabel(k)})),
        });
      }
      out.push({
        key: 'artifacts',
        labelKey: 'pipelines-filter-artifacts',
        options: [
          {key: 'results', label: this.$t('pipelines-filter-has-results')},
          {key: 'mask', label: this.$t('pipelines-filter-has-mask')},
        ],
      });
      return out;
    },
    hasActiveFilters() {
      return Object.values(this.selected).some(v => v.length) || !!this.search.trim();
    },
    visibleRows() {
      let rows = this.allRows.filter(
        r => this.matchesFacets(r, this.selected) && this.matchesSearch(r)
      );
      return rows.slice().sort((a, b) => {
        let ra = sortRank(a, this.isActive(a));
        let rb = sortRank(b, this.isActive(b));
        if (ra !== rb) {
          return ra - rb;
        }
        // Within a group, most recently touched first; "not run" rows fall back to name.
        let ta = a.updated_at || a.created_at || '';
        let tb = b.updated_at || b.created_at || '';
        if (ta !== tb) {
          return ta < tb ? 1 : -1;
        }
        return (a.slide_name || '').localeCompare(b.slide_name || '');
      });
    },
  },
  watch: {
    search() {
      // Otherwise a filter that yields fewer pages leaves you on a blank page 3.
      this.currentPage = 1;
    },
  },
  methods: {
    // ---- row classification -------------------------------------------------------
    isRealRun(row) {
      return row.status !== NOT_RUN;
    },
    isActive(row) {
      return ACTIVE_STATES.includes(row.status);
    },
    isIngesting(row) {
      return ['INGESTING', 'INGEST_RUNNING', 'REINGEST'].includes(row.status);
    },
    canRun(row) {
      // Offered on any terminal-and-unsuccessful row, not just never-run ones. A FAILED
      // run had no way to be retried from this table, which is exactly the state you are
      // in after a run dies at the last step — and where retrying is cheapest, because
      // the results are usually still on disk and get re-used rather than recomputed.
      let retryable = [NOT_RUN, 'FAILED', 'STOPPED'].includes(row.status);
      return retryable && this.catalog.some(p => p.key === row.pipeline);
    },
    runLabel(row) {
      return row.status === NOT_RUN
        ? this.$t('pipelines-run-button')
        : this.$t('pipelines-retry-button');
    },
    canReapply(row) {
      return this.isRealRun(row) && !this.isActive(row) && row.has_results && row.status !== 'FAILED';
    },
    pipelineLabel(key) {
      if (!key) {
        return this.$t('pipeline-unknown');
      }
      // Resolve through the catalog, and never fall back to the raw key — internal
      // identifiers are not client-facing wording.
      let p = this.catalog.find(x => x.key === key);
      if (p) {
        return this.$t(p.labelKey);
      }
      let label = this.$t(`pipeline-${key}`);
      return label === `pipeline-${key}` ? this.$t('pipeline-unknown') : label;
    },
    statusLabel(row) {
      if (row.status === NOT_RUN) {
        return this.$t('pipelines-filter-not-run');
      }
      if (row.phase && this.isActive(row)) {
        let key = `pipelines-phase-${row.phase.toLowerCase()}`;
        let phase = this.$t(key);
        if (phase !== key) {
          return phase;
        }
      }
      return row.status;
    },
    statusType(status) {
      switch (status) {
        case 'FINISHED':
          return 'is-success';
        case 'FAILED':
        case 'DELETING':
          return 'is-danger';
        case 'STOPPED':
        case NOT_RUN:
          return 'is-light';
        case 'RUNNING':
        case 'INFERRING':
        case 'INGESTING':
        case 'INGEST_RUNNING':
        case 'REINGEST':
          return 'is-info';
        default:
          return 'is-warning';
      }
    },
    progressType(row) {
      if (row.status === 'DELETING') {
        return 'is-danger';
      }
      return row.stop_requested ? 'is-warning' : 'is-info';
    },
    percent(row) {
      // Three phases, three units. null renders an indeterminate bar for the genuinely
      // unmeasurable stretches (tissue detection; ingest before the first chunk lands),
      // because a fake 0% reads as stuck rather than working.
      if (row.status === 'DELETING') {
        return row.delete_total
          ? Math.min(100, Math.round((row.deleted_count / row.delete_total) * 100))
          : null;
      }
      if (this.isIngesting(row)) {
        return row.total ? Math.min(100, Math.round((row.inserted / row.total) * 100)) : null;
      }
      if (!row.tiles_total) {
        return null;
      }
      return Math.min(100, Math.round((row.tiles_done / row.tiles_total) * 100));
    },
    etaLabel(row) {
      if (row.status === 'QUEUED') {
        return null;
      }
      if (row.eta_seconds === null || row.eta_seconds === undefined) {
        return row.status === 'INFERRING' ? this.$t('pipelines-eta-calculating') : null;
      }
      return this.$t('pipelines-eta', {time: this.humanDuration(row.eta_seconds)});
    },
    humanDuration(seconds) {
      let s = Math.max(0, Math.round(seconds));
      if (s < 90) {
        return `${s}s`;
      }
      let m = Math.round(s / 60);
      if (m < 90) {
        return `${m} min`;
      }
      let h = Math.floor(m / 60);
      let rem = m % 60;
      return rem ? `${h}h ${rem}m` : `${h}h`;
    },

    // ---- filtering ----------------------------------------------------------------
    matchesSearch(row) {
      let q = this.search.trim().toLowerCase();
      if (!q) {
        return true;
      }
      // Match what is on screen, not the raw keys behind it.
      return [row.slide_name, this.pipelineLabel(row.pipeline), this.statusLabel(row)]
        .some(v => (v || '').toString().toLowerCase().includes(q));
    },
    matchesOption(row, facetKey, optKey) {
      if (facetKey === 'status') {
        switch (optKey) {
          case 'succeeded': return row.status === 'FINISHED';
          case 'failed': return row.status === 'FAILED';
          case 'stopped': return row.status === 'STOPPED';
          case 'not-run': return row.status === NOT_RUN;
          case 'untouched': return !(this.runsByImage[row.image] || []).length;
          case 'running': return this.isActive(row);
          default: return true;
        }
      }
      if (facetKey === 'pipeline') {
        return row.pipeline === optKey;
      }
      if (facetKey === 'artifacts') {
        return optKey === 'results' ? !!row.has_results : !!row.has_mask;
      }
      return true;
    },
    matchesFacets(row, selection, skipFacet) {
      return Object.keys(selection).every(fk => {
        if (fk === skipFacet) {
          return true;              // the facet being counted must not filter itself
        }
        let opts = selection[fk];
        if (!opts.length) {
          return true;              // nothing chosen in this facet means no constraint
        }
        return opts.some(ok => this.matchesOption(row, fk, ok));
      });
    },
    isSelected(facetKey, optKey) {
      return (this.selected[facetKey] || []).includes(optKey);
    },
    toggleFacet(facetKey, optKey) {
      let cur = this.selected[facetKey] || [];
      let next = cur.includes(optKey) ? cur.filter(k => k !== optKey) : [...cur, optKey];
      this.$set(this.selected, facetKey, next);
      this.currentPage = 1;
    },
    clearFilters() {
      Object.keys(this.selected).forEach(k => this.$set(this.selected, k, []));
      this.search = '';
      this.currentPage = 1;
    },
    facetCount(facetKey, optKey) {
      // Counted against every OTHER facet plus the search, so the number is what you
      // would actually see after clicking — not a global total that turns out to be 0.
      return this.allRows.filter(r =>
        this.matchesOption(r, facetKey, optKey)
        && this.matchesFacets(r, this.selected, facetKey)
        && this.matchesSearch(r)
      ).length;
    },

    // ---- data ---------------------------------------------------------------------
    async fetchCatalog() {
      try {
        let {data} = await pipelineService.getCatalog();
        this.catalog = data.pipelines || [];
      } catch (error) {
        console.log(error);
        this.catalog = [];
      }
    },
    async fetchImages() {
      try {
        this.images = (await ImageInstanceCollection.fetchAll({
          filterKey: 'project',
          filterValue: this.project.id
        })).array;
      } catch (error) {
        console.log(error);
        this.images = [];
      }
    },
    async refreshRuns() {
      try {
        let {data} = await pipelineService.listRuns({project: this.project.id});
        this.runs = data.runs || [];
      } catch (error) {
        console.log(error);
      }
    },

    // ---- actions ------------------------------------------------------------------
    async runRow(row) {
      this.running = true;
      try {
        let {data} = await pipelineService.runPipeline({
          pipeline: row.pipeline,
          project: this.project.id,
          images: [row.image],
        });
        let result = (data.runs || [])[0] || {};
        if (['queued', 'running', 'reusing'].includes(result.status)) {
          this.$notify({type: 'success', text: this.$t('pipelines-auto-run-started')});
        } else {
          this.$notify({
            type: 'is-warning',
            text: this.$t('pipelines-auto-run-skipped', {reason: result.reason || result.status})
          });
        }
        await this.refreshRuns();
        this.ensurePolling();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('pipelines-notif-error')});
      } finally {
        this.running = false;
      }
    },
    async stopRun(row) {
      if (!window.confirm(this.$t('pipelines-stop-confirm'))) {
        return;
      }
      try {
        await pipelineService.stopRun(row.run_uuid);
        this.$notify({type: 'success', text: this.$t('pipelines-stopped')});
        await this.refreshRuns();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('pipelines-notif-error')});
      }
    },
    async deleteRun(row) {
      if (!window.confirm(this.$t('pipelines-delete-confirm'))) {
        return;
      }
      try {
        await pipelineService.deleteRun(row.run_uuid);
        await this.refreshRuns();
        this.ensurePolling();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('pipelines-notif-error')});
      }
    },
    async downloadMask(row) {
      try {
        let {data} = await pipelineService.downloadTissueMask(row.run_uuid);
        let name = (row.slide_name || 'slide').replace(/\.[^.]+$/, '') + '_tissue_mask.png';
        let url = window.URL.createObjectURL(new Blob([data], {type: 'image/png'}));
        let a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        this.$notify({type: 'is-warning', text: this.$t('pipelines-mask-unavailable')});
      }
    },
    openReapply(row) {
      this.reapplyRun = row;
      this.reapplyPct = row.insertion_threshold ? Math.round(row.insertion_threshold * 100) : 70;
      this.reapplyModal = true;
    },
    async confirmReapply() {
      let row = this.reapplyRun;
      this.reapplyModal = false;
      if (!row) {
        return;
      }
      try {
        await pipelineService.reapplyThreshold(row.run_uuid, this.reapplyPct / 100);
        this.$notify({
          type: 'success',
          text: this.$t('pipelines-reapply-started', {pct: this.reapplyPct})
        });
        await this.refreshRuns();
        this.ensurePolling();
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('pipelines-notif-error')});
      }
    },
    ensurePolling() {
      if (this.pollTimer) {
        return;
      }
      this.pollTimer = setInterval(async () => {
        await this.refreshRuns();
        if (!this.hasActiveRun) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
      }, 3000);
    },
  },
  async created() {
    if (!this.canManageProject) {
      this.loading = false;
      return;
    }
    try {
      await this.fetchCatalog();
      await this.fetchImages();
      await this.refreshRuns();
      if (this.hasActiveRun) {
        this.ensurePolling();
      }
    } catch (error) {
      console.log(error);
    }
    this.loading = false;
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
};
</script>

<style scoped>
.pipelines-page {
  padding: 1.5rem;
}

.box h2 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0;
}

.runs-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.runs-search {
  max-width: 20rem;
  flex: 1 1 12rem;
}

.runs-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  margin: 0.5rem 0 0.75rem;
}

.facet {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.facet-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7a7a7a;
  margin-right: 0.15rem;
}

.facet-chip {
  border-radius: 999px;
}

.facet-count {
  margin-left: 0.4rem;
  opacity: 0.65;
  font-variant-numeric: tabular-nums;
}

.matrix-note {
  color: #7a7a7a;
  margin-bottom: 0.75rem;
}

.queue-note {
  color: #7a7a7a;
  margin-top: 0.75rem;
  margin-bottom: 0;
}

.progress-cell {
  min-width: 10rem;
}

.progress-note {
  font-size: 0.75rem;
  color: #7a7a7a;
  margin-top: 0.15rem;
  font-variant-numeric: tabular-nums;
}

.run-actions {
  margin-bottom: 0;
}

.run-actions .button {
  margin-bottom: 0.25rem;
}

.threshold-value {
  min-width: 3.2rem;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  line-height: 2.25rem;
}

.reapply-card {
  width: 32rem;
  max-width: 100%;
}

.reapply-field {
  margin-top: 1rem;
  align-items: center;
}
</style>
